import type { IMediaFolder, IMediaFolderUser } from "@/shared/types";
import { api } from "./api";

export const mediaFoldersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMediaFolders: builder.query<
      IMediaFolder[],
      | {
          archived?: boolean;
          limit?: number;
          signal?: AbortSignal;
        }
      | undefined
    >({
      query: ({ signal, archived, limit } = { archived: false }) => ({
        url: "/media-folders",
        method: "GET",
        signal,
        params: { archived, limit },
      }),
    }),
    getMediaFolder: builder.query<
      IMediaFolder | null,
      {
        id: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, id }) => ({
        url: `/media-folders/${id}`,
        method: "GET",
        signal,
      }),
    }),
    updateMediaFolder: builder.mutation<
      IMediaFolder | null,
      {
        id: string;
        title?: string;
        archived?: boolean;
        signal?: AbortSignal;
        users?: IMediaFolderUser[] | null;
        openAccess?: boolean;
        parentId?: string | null;
      }
    >({
      query: ({
        signal,
        id,
        archived,
        title,
        users,
        openAccess,
        parentId,
      }) => ({
        url: `/media-folders/${id}`,
        method: "PUT",
        signal,
        body: { title, archived, users, openAccess, parentId },
      }),
      async onQueryStarted({ archived, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data) return;

          dispatch(
            mediaFoldersApi.util.updateQueryData(
              "getMediaFolders",
              { archived: archived == null ? data.archived : !archived },
              (draft) => {
                const idx = draft.findIndex((el) => el._id === id);
                if (idx >= 0) {
                  draft[idx] = {
                    ...draft[idx],
                    ...data,
                  };
                }
              },
            ),
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
    createMediaFolder: builder.mutation<
      IMediaFolder,
      {
        signal?: AbortSignal;
        title: string;
        parentId?: string | undefined;
        users?: IMediaFolderUser[] | null;
        openAccess?: boolean;
      }
    >({
      query: ({ signal, title, parentId, users, openAccess }) => ({
        url: "/media-folders",
        method: "POST",
        signal,
        body: {
          title,
          parentId,
          users,
          openAccess,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (!data) return;

          dispatch(
            mediaFoldersApi.util.updateQueryData(
              "getMediaFolders",
              { archived: false },
              (draft) => {
                draft.unshift(data);
              },
            ),
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
    archiveMediaFolder: builder.mutation<
      { foldersIds: string[] } | null,
      {
        id: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, id }) => ({
        url: `/media-folders/archive/${id}`,
        method: "PUT",
        signal,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const foldersIds = data?.foldersIds ?? [];
          if (foldersIds.length === 0) return;

          dispatch(
            mediaFoldersApi.util.updateQueryData(
              "getMediaFolders",
              { archived: false },
              (draft) => {
                for (const id of foldersIds) {
                  const idx = draft.findIndex((el) => el._id === id);
                  if (idx >= 0) {
                    draft[idx].archived = true;
                  }
                }
              },
            ),
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
    restoreMediaFolder: builder.mutation<
      { foldersIds: string[] } | null,
      {
        id: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, id }) => ({
        url: `/media-folders/restore/${id}`,
        method: "PUT",
        signal,
      }),
      async onQueryStarted({ id: topFolderId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const foldersIds = data?.foldersIds ?? [];
          if (foldersIds.length === 0) return;

          dispatch(
            mediaFoldersApi.util.updateQueryData(
              "getMediaFolders",
              { archived: true },
              (draft) => {
                for (const id of foldersIds) {
                  const idx = draft.findIndex((el) => el._id === id);
                  if (idx >= 0) {
                    if (id === topFolderId) {
                      draft[idx].parentId = null;
                    }
                    draft[idx].archived = false;
                  }
                }
              },
            ),
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
    deleteMediaFolder: builder.mutation<
      { foldersIds: string[] } | null,
      {
        id: string;
        signal?: AbortSignal;
      }
    >({
      query: ({ signal, id }) => ({
        url: `/media-folders/${id}`,
        method: "DELETE",
        signal,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const foldersIds = data?.foldersIds || [];
          if (foldersIds.length === 0) return;

          dispatch(
            mediaFoldersApi.util.updateQueryData(
              "getMediaFolders",
              { archived: true },
              (draft) => {
                for (const id of foldersIds) {
                  const idx = draft.findIndex((el) => el._id === id);
                  if (idx >= 0) {
                    draft.splice(idx, 1);
                  }
                }
              },
            ),
          );
        } catch (e) {
          console.log(e);
        }
      },
    }),
  }),
});

export const {
  useGetMediaFoldersQuery,
  useLazyGetMediaFolderQuery,
  useUpdateMediaFolderMutation,
  useCreateMediaFolderMutation,
  useDeleteMediaFolderMutation,
  useArchiveMediaFolderMutation,
  useRestoreMediaFolderMutation,
} = mediaFoldersApi;
